// Schreiben-Assistent Etappe 2: Briefköpfe und Absender pflegen (nur Admins – RLS). Bilder:
// Briefpapier als A4-JPG (150 dpi) im Bucket briefpapier/briefkoepfe/, Unterschrift im Bucket
// signaturen/brief-absender/ (JPG, ohne Transparenz). Ein PDF-Briefpapier wird mit pdf.js in ein Bild der ersten Seite umgewandelt.
import { supabase } from '../supabase/client';
import { ladePdfjs } from '../pdf/pdfjs';
import { istAdmin } from './rechte';
import { BRIEFKOPF_BUCKET, UNTERSCHRIFT_BUCKET, vergissBild, type Absender, type Briefkopf } from './gestaltung';

const A4 = { breite: 1240, hoehe: 1754 } as const; // 150 dpi

function canvasZuBlob(c: HTMLCanvasElement, typ: string, qualitaet?: number): Promise<Blob> {
  return new Promise((ok, fehler) => c.toBlob((b) => (b ? ok(b) : fehler(new Error('Bild konnte nicht erzeugt werden'))), typ, qualitaet));
}

async function bildAusDatei(datei: File): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(datei);
  try {
    const img = new Image();
    await new Promise<void>((ok, fehler) => { img.onload = () => ok(); img.onerror = () => fehler(new Error('Bild nicht lesbar')); img.src = url; });
    return img;
  } finally { setTimeout(() => URL.revokeObjectURL(url), 0); }
}

/** Briefpapier (PDF, JPG, PNG) → A4-JPG 150 dpi auf weißem Grund. */
export async function briefpapierBild(datei: File): Promise<Blob> {
  const c = document.createElement('canvas');
  c.width = A4.breite; c.height = A4.hoehe;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, c.width, c.height);
  if (datei.type === 'application/pdf' || /\.pdf$/i.test(datei.name)) {
    const pdfjs = await ladePdfjs();
    const doc = await pdfjs.getDocument({ data: await datei.arrayBuffer() }).promise;
    const seite = await doc.getPage(1);
    const basis = seite.getViewport({ scale: 1 });
    const viewport = seite.getViewport({ scale: A4.breite / basis.width });
    const tmp = document.createElement('canvas');
    tmp.width = Math.round(viewport.width); tmp.height = Math.round(viewport.height);
    const tctx = tmp.getContext('2d')!;
    tctx.fillStyle = '#fff'; tctx.fillRect(0, 0, tmp.width, tmp.height);
    await seite.render({ canvasContext: tctx, viewport, canvas: tmp } as never).promise;
    ctx.drawImage(tmp, 0, 0, A4.breite, A4.hoehe);
  } else {
    ctx.drawImage(await bildAusDatei(datei), 0, 0, A4.breite, A4.hoehe);
  }
  return canvasZuBlob(c, 'image/jpeg', 0.88);
}

/** Unterschrift (JPG/PNG) → JPG auf Weiß, höchstens 900 px breit. Bewusst ohne Transparenz: die E-POST prüft auf
 *  PDF/A, und durchsichtige Bilder (Alphakanal) sind dort nicht erlaubt. Der Textbereich des Briefpapiers ist weiß. */
export async function unterschriftBild(datei: File): Promise<Blob> {
  const img = await bildAusDatei(datei);
  const faktor = Math.min(1, 900 / img.naturalWidth);
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(img.naturalWidth * faktor)); c.height = Math.max(1, Math.round(img.naturalHeight * faktor));
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, c.width, c.height);
  ctx.drawImage(img, 0, 0, c.width, c.height);
  return canvasZuBlob(c, 'image/jpeg', 0.92);
}

async function hochladen(bucket: string, pfad: string, blob: Blob): Promise<void> {
  const { error } = await supabase.storage.from(bucket).upload(pfad, blob, { upsert: true, contentType: blob.type });
  if (error) throw new Error(`Bild nicht hochgeladen: ${error.message}`);
  vergissBild(bucket, pfad);
}

type KopfEingabe = Pick<Briefkopf, 'name' | 'ruecksendezeile' | 'ort' | 'ist_standard' | 'aktiv'>;

/** Anlegen (id null) oder ändern; datei = neues Briefpapier. „Standard“ kann es nur einmal geben. */
export async function speichereBriefkopf(id: string | null, d: KopfEingabe, datei: File | null): Promise<void> {
  if (!d.name.trim()) throw new Error('Bitte einen Namen eingeben.');
  if (!d.ruecksendezeile.trim()) throw new Error('Bitte die Rücksendezeile eingeben.');
  const werte = { name: d.name.trim(), ruecksendezeile: d.ruecksendezeile.trim(), ort: d.ort.trim() || 'Kirchhain', aktiv: d.aktiv, updated_at: new Date().toISOString() };
  let kopfId = id;
  if (!kopfId) {
    const { data, error } = await supabase.from('briefkoepfe').insert({ ...werte, ist_standard: false }).select('id').single();
    if (error || !data) throw new Error(error?.message ?? 'Briefkopf nicht angelegt');
    kopfId = data.id as string;
  } else {
    const { error } = await supabase.from('briefkoepfe').update(werte).eq('id', kopfId);
    if (error) throw new Error(error.message);
  }
  if (datei) {
    const pfad = `briefkoepfe/${kopfId}-${Date.now()}.jpg`;
    await hochladen(BRIEFKOPF_BUCKET, pfad, await briefpapierBild(datei));
    const { error } = await supabase.from('briefkoepfe').update({ bild_pfad: pfad }).eq('id', kopfId);
    if (error) throw new Error(error.message);
  }
  if (d.ist_standard) {
    // erst die anderen zurücksetzen (eindeutiger Index auf ist_standard)
    const { error: e1 } = await supabase.from('briefkoepfe').update({ ist_standard: false }).neq('id', kopfId).eq('ist_standard', true);
    if (e1) throw new Error(e1.message);
    const { error: e2 } = await supabase.from('briefkoepfe').update({ ist_standard: true, aktiv: true }).eq('id', kopfId);
    if (e2) throw new Error(e2.message);
  }
}

type AbsenderEingabe = Pick<Absender, 'name' | 'funktion' | 'profile_id' | 'aktiv' | 'grussformel' | 'im_auftrag'>;

export async function speichereAbsender(id: string | null, d: AbsenderEingabe, datei: File | null, unterschriftEntfernen = false): Promise<void> {
  if (!d.name.trim()) throw new Error('Bitte einen Namen eingeben.');
  const basis = { name: d.name.trim(), funktion: d.funktion?.trim() || null, profile_id: d.profile_id || null, aktiv: d.aktiv, updated_at: new Date().toISOString(),
    ...(unterschriftEntfernen ? { unterschrift_pfad: null } : {}) };
  // grussformel erst mit der Migration – fehlt die Spalte (PGRST204), ohne sie speichern
  const mitGruss = {
    ...basis,
    ...(d.grussformel !== undefined ? { grussformel: d.grussformel?.trim() || null } : {}),
    // #608: i. A. erst mit der Migration 2026-11-04-absender-im-auftrag
    ...(d.im_auftrag !== undefined ? { im_auftrag: !!d.im_auftrag } : {}),
  };
  const schreibe = async (werte: Record<string, unknown>) => (id
    ? supabase.from('brief_absender').update(werte).eq('id', id).select('id').single()
    : supabase.from('brief_absender').insert(werte).select('id').single());
  let r = await schreibe(mitGruss);
  if (r.error?.code === 'PGRST204') r = await schreibe(basis);
  if (r.error || !r.data) throw new Error(r.error?.message ?? 'Absender nicht gespeichert');
  const absId = r.data.id as string;
  if (datei) {
    const pfad = `brief-absender/${absId}-${Date.now()}.jpg`;
    await hochladen(UNTERSCHRIFT_BUCKET, pfad, await unterschriftBild(datei));
    const { error } = await supabase.from('brief_absender').update({ unterschrift_pfad: pfad }).eq('id', absId);
    if (error) throw new Error(error.message);
  }
}

/** Vorschau-URL für die Pflege (Briefpapier öffentlich, Unterschrift signiert). */
export async function bildUrl(bucket: string, pfad: string): Promise<string | null> {
  if (bucket === BRIEFKOPF_BUCKET) return supabase.storage.from(bucket).getPublicUrl(pfad).data.publicUrl;
  const { data } = await supabase.storage.from(bucket).createSignedUrl(pfad, 600);
  return data?.signedUrl ?? null;
}

/** Mitarbeiter fürs Verknüpfen (Absender wird für sie vorgewählt). */
export async function ladeMitarbeiter(): Promise<{ id: string; name: string }[]> {
  const { data } = await supabase.from('profiles').select('id, full_name, email, role')
    .in('role', ['admin', 'superadmin', 'super admin', 'master admin', 'team']).order('full_name');
  return ((data ?? []) as { id: string; full_name: string | null; email: string | null }[])
    .map((p) => ({ id: p.id, name: p.full_name?.trim() || p.email || p.id }));
}

/** Pflegen dürfen nur Admins (wie die RLS is_admin) – Mitarbeiter sehen die Liste nur. */
export const darfGestaltungPflegen = istAdmin;
